import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plural, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'

import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'

import useInterval from '@/hooks/useInterval'

dayjs.extend(customParseFormat)
dayjs.extend(duration)

export type WaitingDoctorPaneProps = {
  avgWaitingTime?: number
  isRefreshing: boolean
  lastUpdated: Date
  noAvailableDoctors: boolean
  onRefresh: () => void
  peopleAhead?: number
  reservedAt: string
  waitingRoomName: string
}

export function WaitingDoctorPane(props: WaitingDoctorPaneProps) {
  const { avgWaitingTime, isRefreshing, lastUpdated, noAvailableDoctors, onRefresh, peopleAhead, reservedAt, waitingRoomName } = props
  useLingui()
  const { t } = useTranslation()

  const [currentTime, setCurrentTime] = useState<Dayjs>(dayjs())

  useInterval(() => {
    setCurrentTime(dayjs())
  }, 1000)

  const waitedTime = dayjs.duration(currentTime.diff(dayjs(reservedAt))).format('mm:ss')
  const timeSinceUpdate = dayjs.duration(currentTime.diff(dayjs(lastUpdated))).format('mm:ss')

  return (
    <Stack alignItems="center" justifyContent="center">
      <Stack direction="row" alignItems="center" justifyContent="space-between" paddingX={2.5} position="absolute" top={20} width="100%">
        <Box fontSize={20} fontWeight={600}>
          <Trans>Waiting room: {waitingRoomName}</Trans>
        </Box>
        <Stack direction="row" spacing={1}>
          {typeof peopleAhead !== 'undefined' && (
            <Chip
              color="secondary"
              label={
                peopleAhead < 1 ? (
                  <Trans>You are next</Trans>
                ) : (
                  <Plural value={peopleAhead} one="There is one person before you" other="There are # persons before you" />
                )
              }
              variant="outlined"
            />
          )}
          <Chip color="primary" icon={<HourglassTopRoundedIcon />} label={waitedTime} variant="outlined" />
        </Stack>
      </Stack>
      <HeartbeatLoader />
      <Stack alignItems="center" spacing={1}>
        {noAvailableDoctors ? (
          <Typography>{t('page.liveConsultation.waitingDoctorPane.noAvailableDoctorsMessage')}</Typography>
        ) : typeof avgWaitingTime !== 'undefined' ? (
          <Typography>
            {t('page.liveConsultation.waitingDoctorPane.waitingMessage', {
              count: avgWaitingTime,
              context: `${avgWaitingTime}`,
            })}
          </Typography>
        ) : null}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" color="text.secondary">
            <Trans>Updated {timeSinceUpdate} ago</Trans>
          </Typography>
          <Tooltip title={<Trans>Refresh</Trans>}>
            <span>
              <IconButton size="small" onClick={onRefresh} disabled={isRefreshing}>
                <RefreshRoundedIcon
                  fontSize="small"
                  sx={{
                    animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default WaitingDoctorPane
